import React, { useState, useEffect } from "react";
import { Spin, message, Card, Table, Select, Button, Tooltip, Modal, Input } from "antd";
import { DownloadOutlined, LockOutlined } from "@ant-design/icons";
import { getMailFilesTimelineReport, getMailingLists, getMailFilesTimelinePeopleDetails } from "../../core/api/database";
import { useSelector } from "react-redux";
import MailingListFilter from './components/MailingListFilter';
import categoryOptions from './constants/categoryOptions';
import { exportToExcel, getFormattedDate } from "../../utils/exportUtils";

const MFvsTimelineTab = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(['all']);
  const [mailingListOptions, setMailingListOptions] = useState([]);
  const [selectedMailingLists, setSelectedMailingLists] = useState(['all']);
  const [loadingMailingLists, setLoadingMailingLists] = useState(false);
  const [selectedListCount, setSelectedListCount] = useState('all');
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [downloadPassword, setDownloadPassword] = useState('');
  const [currentActiveYears, setCurrentActiveYears] = useState(null);
  
  // Get selectedClients from Redux store
  const selectedClients = useSelector((state) => state.selectedClients);

  useEffect(() => {
    fetchMailingLists();
    // Only fetch report data on initial load
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Automatically fetch data when selectedClients changes
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClients]);

  const fetchMailingLists = async () => {
    setLoadingMailingLists(true);
    try {
      const response = await getMailingLists();
      const lists = response.data;
      
      // Format lists for the Select component
      const formattedOptions = lists.map(list => ({
        label: list,
        value: list
      }));
      
      setMailingListOptions(formattedOptions);
      setLoadingMailingLists(false);
    } catch (error) {
      console.error("Error fetching mailing lists:", error);
      message.error("Failed to fetch mailing lists");
      setLoadingMailingLists(false);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      console.log('Fetching timeline data with params:', {
        selectedClients,
        selectedCategory,
        selectedMailingLists,
        selectedListCount
      });
      
      const response = await getMailFilesTimelineReport(
        selectedClients, 
        selectedCategory, 
        selectedMailingLists,
        selectedListCount
      );
      
      console.log('Timeline API response:', response);
      
      if (response && response.data) {
        // Add download handler to each row
        const dataWithDownloadHandler = (response.data.data || []).map(item => ({
          ...item,
          onDownload: handleDownloadPeopleDetails
        }));
        
        setReportData(dataWithDownloadHandler);
        setTotalCustomers(response.data.total_unique_customers || 0);
      } else {
        console.error('Invalid response format:', response);
        message.error('Received invalid data format from server');
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching mail files timeline report data:", error);
      message.error(`Failed to fetch timeline data: ${error.message || 'Unknown error'}`);
      setReportData([]);
      setTotalCustomers(0);
      setLoading(false);
    }
  };
  
  const handleCategoryChange = (value) => {
    // If 'all' is selected, deselect all other options
    if (value.includes('all') && !selectedCategory.includes('all')) {
      setSelectedCategory(['all']);
    } 
    // If 'all' is deselected and there are other selections, keep only those
    else if (!value.includes('all') && selectedCategory.includes('all')) {
      const newSelection = value.filter(item => item !== 'all');
      setSelectedCategory(newSelection.length > 0 ? newSelection : ['all']);
    }
    // Normal case: update selection
    else {
      setSelectedCategory(value);
    }
  };
  
  const handleListCountChange = (value) => {
    setSelectedListCount(value);
  };
  
  const handleMailingListsChange = (value) => {
    // If 'all' is selected, deselect all other options
    if (value.includes('all') && !selectedMailingLists.includes('all')) {
      setSelectedMailingLists(['all']);
    } 
    // If 'all' is deselected and there are other selections, keep only those
    else if (!value.includes('all') && selectedMailingLists.includes('all')) {
      const newSelection = value.filter(item => item !== 'all');
      setSelectedMailingLists(newSelection.length > 0 ? newSelection : ['all']);
    }
    // Normal case: update selection
    else {
      setSelectedMailingLists(value);
    }
  };
  
  const handleSelectAllMailingLists = () => {
    setSelectedMailingLists(['all']);
  };
  
  const handleDeselectAllMailingLists = () => {
    if (mailingListOptions.length > 0) {
      const allValues = mailingListOptions.map(option => option.value);
      setSelectedMailingLists(allValues);
    }
  };
  
  const handleApplyFilters = () => {
    fetchReportData();
  };
  
  const handleClearFilters = () => {
    setSelectedCategory(['all']);
    setSelectedMailingLists(['all']);
    setSelectedListCount('all');
    // After clearing filters, apply them immediately
    setTimeout(() => fetchReportData(), 0);
  };

  const handleExportToExcel = () => {
    const fileName = `Mail_Files_Timeline_Report_${getFormattedDate()}`;
    const data = reportData.map((row) => ({
      'Active Years': row.active_years,
      'Customer Count': row.customer_count
    }));
    
    const columns = [
      { title: 'Active Years', dataIndex: 'Active Years' },
      { title: 'Customer Count', dataIndex: 'Customer Count' }
    ];
    
    exportToExcel(data, fileName, columns);
  };
  
  // Show password modal before downloading
  const showPasswordModal = (activeYears) => {
    setCurrentActiveYears(activeYears);
    setPasswordModalVisible(true);
    setDownloadPassword('');
  };

  // Handle password modal cancel
  const handlePasswordModalCancel = () => {
    setPasswordModalVisible(false);
    setDownloadPassword('');
    setCurrentActiveYears(null);
  };

  // Handle password input change
  const handlePasswordChange = (e) => {
    setDownloadPassword(e.target.value);
  };

  // Handle password submit
  const handlePasswordSubmit = () => {
    if (!downloadPassword.trim()) {
      message.error('Please enter the download password');
      return;
    }
    
    setPasswordModalVisible(false);
    handleDownloadPeopleDetails(currentActiveYears, downloadPassword);
  };
  
  // Download people details for a specific set of active years
  const handleDownloadPeopleDetails = async (activeYears, password) => {
    setDownloadLoading(true);
    try {
      const response = await getMailFilesTimelinePeopleDetails(
        activeYears,
        selectedClients, 
        selectedCategory, 
        selectedMailingLists,
        password
      );
      
      if (response.data.data.length === 0) {
        message.info("No customer data available for these active years");
        setDownloadLoading(false);
        return;
      }
      
      // Format the data for Excel export
      const peopleData = response.data.data.map(person => ({
        'Person ID': person.person_id,
        'Full Name': person.full_name || '',
        'Email': person.email || '',
        'Phone': person.phone || '',
        'Address': person.address || '',
        'City': person.city || '',
        'State': person.state || '',
        'Zip': person.zip || '',
        'Country': person.country || '',
        'Active Years': person.active_years ? person.active_years.join(', ') : '',
        'Mailing Lists': person.mailing_lists ? person.mailing_lists.join(', ') : ''
      }));
      
      // Create Excel file name
      const fileName = `Customers_Active_In_${activeYears.replace(/,\s*/g, '_')}_${getFormattedDate()}`;
      
      // Define columns for the Excel file
      const peopleColumns = [
        { title: 'Person ID', dataIndex: 'Person ID' },
        { title: 'Full Name', dataIndex: 'Full Name' },
        { title: 'Email', dataIndex: 'Email' },
        { title: 'Phone', dataIndex: 'Phone' },
        { title: 'Address', dataIndex: 'Address' },
        { title: 'City', dataIndex: 'City' },
        { title: 'State', dataIndex: 'State' },
        { title: 'Zip', dataIndex: 'Zip' },
        { title: 'Country', dataIndex: 'Country' },
        { title: 'Active Years', dataIndex: 'Active Years' },
        { title: 'Mailing Lists', dataIndex: 'Mailing Lists' }
      ];
      
      // Export to Excel
      exportToExcel(peopleData, fileName, peopleColumns);
      
      message.success(`Downloaded details for customers active in years: ${activeYears}`);
      setDownloadLoading(false);
    } catch (error) {
      console.error("Error downloading customer details:", error);
      if (error.response && error.response.status === 403) {
        message.error('Invalid password. Please try again with the correct password.');
      } else {
        message.error(`Failed to download customer details: ${error.message || 'Unknown error'}`);
      }
      setDownloadLoading(false);
    }
  };

  // Define columns for the timeline table
  const columns = [
    {
      title: 'Active Years',
      dataIndex: 'active_years',
      key: 'active_years',
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => {
        // Sort by number of years (descending), then alphabetically
        const aYears = a.active_years.split(', ').length;
        const bYears = b.active_years.split(', ').length;
        if (aYears !== bYears) return bYears - aYears;
        return a.active_years.localeCompare(b.active_years);
      },
    },
    {
      title: 'Customer Count',
      dataIndex: 'customer_count',
      key: 'customer_count',
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => a.customer_count - b.customer_count,
    },
    {
      title: 'Download',
      key: 'download',
      render: (_, record) => (
        <Tooltip title="Download customer details for these years">
          <Button
            type="link"
            icon={<DownloadOutlined />}
            loading={downloadLoading && currentActiveYears === record.active_years}
            onClick={() => showPasswordModal(record.active_years)}
          />
        </Tooltip>
      ),
    }
  ];

  return (
    <div className="mf-vs-timeline-tab">
      <Card className="filter-card mb-4">
        <div className="row align-items-center">
          <div className="col-md-3 mb-3 mb-md-0">
            <label className="form-label">Category</label>
            <Select
              mode="multiple"
              value={selectedCategory}
              onChange={handleCategoryChange}
              options={categoryOptions}
              className="w-100"
              placeholder="Select Category"
              maxTagCount="responsive"
            />
          </div>



       
          
          <div className="col-md-3 mb-3 mb-md-0">
            <label className="form-label">List Count</label>
            <Select
              value={selectedListCount}
              onChange={handleListCountChange}
              className="w-100"
              placeholder="Select List Count"
              options={[
                { value: 'all', label: 'All List Counts' },
                { value: '1', label: '1 List' },
                { value: '2', label: '2 Lists' },
                { value: '3', label: '3 Lists' },
                { value: '4', label: '4 Lists' },
                { value: '5', label: '5 Lists' },
                { value: '6', label: '6+ Lists' }
              ]}
            />
          </div>
          
          <div className="col-md-3 mb-3 mb-md-0">
            <label className="form-label">Mailing Lists</label>
            <MailingListFilter 
              mailingListOptions={mailingListOptions}
              selectedMailingLists={selectedMailingLists}
              onMailingListsChange={handleMailingListsChange}
              loadingMailingLists={loadingMailingLists}
              onSelectAll={handleSelectAllMailingLists}
              onDeselectAll={handleDeselectAllMailingLists}
            />
          </div>
          
          <div className="col-md-4 d-flex align-items-end">
            <div className="d-flex gap-2 mt-md-4">
              <Button 
                type="primary" 
                onClick={handleApplyFilters}
              >
                Apply Filters
              </Button>
              <Button 
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </Card>
      
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Total Unique Customers: {totalCustomers}</h5>
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleExportToExcel}
          disabled={reportData.length === 0}
        >
          Export to Excel
        </Button>
      </div>
      
      <Spin spinning={loading}>
        <Table 
          dataSource={reportData} 
          columns={columns}
          rowKey={(record) => record.active_years}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Spin>
      
      {/* Password Modal */}
      <Modal
        title="Password Required"
        open={passwordModalVisible}
        onCancel={handlePasswordModalCancel}
        footer={[
          <Button key="cancel" onClick={handlePasswordModalCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handlePasswordSubmit}>
            Download
          </Button>,
        ]}
      >
        <p>Please enter the password to download customer data:</p>
        <Input.Password
          prefix={<LockOutlined />}
          value={downloadPassword}
          onChange={handlePasswordChange}
          placeholder="Enter password"
          onPressEnter={handlePasswordSubmit}
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default MFvsTimelineTab;
